import React, { useEffect, useState } from 'react';

import ReactHtmlParser from 'react-html-parser';

import { getSizedImageUrl } from '../../../../../scripts/utils/image';

import { useSubBuilder } from '../../../../context/subBuilder/useSubBuilder';
import {
  aysncGetRCProductOptions,
  getXWorkingDaysBeforeDate,
} from '../../../../context/subBuilder/utils';
import { ProductVariant } from '../../../../util/typings';
import Price from '../../../Price';
import IconReviewStar from '../../../svgs/ReviewStar';

import RechargeAddToCart from '../../RechargeAddToCart';
import BackgroundBlackOutlineLarge from '../svgs/BlackOutlineLarge';
import YellowUnderline from '../svgs/YellowUnderline';

const Summary: React.FunctionComponent = () => {
  const { settings, state, getters, actions } = useSubBuilder();

  const [variant, setVariant] = useState(null as ProductVariant | null);

  const [unit, setUnit] = useState('month' as 'week' | 'month' | 'day');

  useEffect(() => {
    const variant =
      state.subscriptionType === 'me'
        ? getters.getCurrentVariant()
        : getters.getCurrentGiftVariant();

    if (variant) {
      setVariant(variant);
    }
  }, []);

  useEffect(() => {
    if (state.subscriptionType === 'gift') {
      return;
    }
    aysncGetRCProductOptions(state.productHandle).then((setup) => {
      if (setup.unit) {
        // do we have a unit set up in RC
        setUnit(setup.unit);
      }
    });
  }, [state.productHandle, state.subscriptionType]);

  const stepConfig = settings.steps.summary;

  if (!variant) return null;

  const copyDate = state.deliveryDate
    ? new Date(state.deliveryDate.getTime())
    : null;

  const approxSecondDeliveryDate = copyDate
    ? new Date(copyDate.setMonth(copyDate.getMonth() + 1))
    : null; // 1 month in future

  const orderDate = approxSecondDeliveryDate
    ? getXWorkingDaysBeforeDate(
      settings.steps.date.settings.deliveryTime,
      approxSecondDeliveryDate,
      settings.steps.date.excludedDates,
    )
    : null;

  const chargeDelay = Math.max(
    0,
    orderDate
      ? Math.ceil(
        orderDate.getTime() / (1000 * 3600 * 24) -
            new Date().getTime() / (1000 * 3600 * 24),
      )
      : 0,
  );

  const currentSubProduct = [
    ...settings.products.gift,
    ...settings.products.me,
  ].find(
    (prod) =>
      prod.product.handle ===
      (state.subscriptionType === 'me'
        ? state.productHandle
        : state.giftProductHandle),
  )?.product;
  const productTitle = [
    ...settings.products.gift,
    ...settings.products.me,
  ].find(
    (prod) =>
      prod.product.handle ===
      (state.subscriptionType === 'me'
        ? state.productHandle
        : state.giftProductHandle),
  ).title;
  const kgVariant = getters.getKgVariant();
  const cartVariant = kgVariant || variant;
  const cartQuantity = kgVariant ? state.quantityOption / 4 : state.quantityOption;
  const summaryTotalPrice = cartVariant.price * cartQuantity;
  const summaryOriginalTotalPrice = kgVariant ? variant.price * state.quantityOption : summaryTotalPrice;
  const sellingPlanIdForAddToCart = cartVariant.selling_plan_allocations[0].selling_plan_id;
  const addToCartProperties = {
    // eslint-disable-next-line camelcase
    _order_count: '1',
    _charge_delay: chargeDelay.toString(),
    // eslint-disable-next-line camelcase
    _subscription_type: state.subscriptionType,
    // eslint-disable-next-line camelcase
    ...(kgVariant ? {
      _is_kg_product: 'true',
      // Original per-unit price = 4 x 250g bag price, used by minicart for strike-through/savings display
      _kg_original_unit_price: String(variant.price * 4),
    } : {}),
    // eslint-disable-next-line camelcase
    ...(state.deliveryDate
      ? { _delivery_date: state.deliveryDate.toString() }
      : {}),
  };

  return (
    <div className="sub-builder-summary">
      <div className="sub-builder-summary-image">
        {(stepConfig.settings.image || currentSubProduct?.featured_image) && (
          <img
            src={getSizedImageUrl(stepConfig.settings.image || currentSubProduct?.featured_image, '2000x2000')}
            data-lazy-load
          />
        )}
      </div>
      <div className="sub-builder-summary-info">
        <div className="sub-builder-summary-info-inner">
          <h2 className="heading-3 sub-builder-summary-title relative">
            {stepConfig.strings.title}
            <YellowUnderline />
          </h2>
          <div className="relative sub-builder-plan-list-wrap">
            <BackgroundBlackOutlineLarge />
            <ul className="sub-builder-plan-list">
              <li>
                <span>{stepConfig.strings.whoLabel}</span>:{' '}
                <span>
                  {state.subscriptionType === 'gift' ? 'Gift' : 'You'}
                </span>
              </li>
              <li>
                <span>{stepConfig.strings.planLabel}</span>:{' '}
                <span>{productTitle}</span>
              </li>
              <li>
                <span>{stepConfig.strings.grindLabel}</span>:{' '}
                <span>
                  {state.subscriptionType === 'me'
                    ? state.grindOption
                    : state.giftGrindOption}
                </span>
              </li>
              {state.durationOption ||
                (state.durationGiftOption && (
                  <li>
                    <span>{stepConfig.strings.durationLabel}</span>:{' '}
                    <span>
                      {state.subscriptionType === 'me'
                        ? state.durationOption
                        : state.durationGiftOption}{' '}
                      {unit}s
                    </span>
                  </li>
                ))}
              {state.subscriptionType === 'me' && (
                <li>
                  <span>{stepConfig.strings.frequencyLabel}</span>:{' '}
                  <span>
                    Every{' '}
                    {state.intervalFrequency > 1
                      ? `${state.intervalFrequency} `
                      : ''}
                    {unit}
                    {state.intervalFrequency > 1 && 's'}
                  </span>
                </li>
              )}
              <li>
                <span>{stepConfig.strings.quantityLabel}</span>
                <span>
                  :{' '}
                  {kgVariant
                    ? (() => {
                      const kgBagCount = (state.quantityOption / 4) * (state.isBothProducts ? 2 : 1);
                      return `${kgBagCount} x 1kg bag${kgBagCount > 1 ? 's' : ''}`;
                    })()
                    : (() => {
                      const bagCount = state.quantityOption * (state.isBothProducts ? 2 : 1);
                      return `${bagCount} x 250g bag${bagCount > 1 ? 's' : ''}`;
                    })()}
                </span>
              </li>
              {state.subscriptionType === 'gift' && (
                <li>
                  <span>{stepConfig.strings.deliveryLabel}</span>:{' '}
                  <span>{`${state.deliveryDate.getDate()}/${state.deliveryDate.getMonth() + 1}/${state.deliveryDate.getFullYear()}`}</span>
                </li>
              )}
            </ul>
            <button
              className="go-back-cta"
              onClick={() => actions.moveStep(-1)}
            >
              Go back and edit
            </button>
          </div>

          <div className="sub-builder-summary-price">
            <span>{stepConfig.strings.priceLabel}</span>
            <span>
              <Price
                originalPrice={summaryOriginalTotalPrice}
                finalPrice={summaryTotalPrice}
                priceVaries={false}
              />
            </span>
          </div>

          <RechargeAddToCart
            quantity={cartQuantity}
            variantId={cartVariant.id}
            sellingPlanId={sellingPlanIdForAddToCart}
            chargeDelay={chargeDelay}
            numberChargesUntilExpiration={
              state.subscriptionType === 'gift' ? state.durationGiftOption : 0
            }
            className="btn btn--primary sub-builder-atc"
            extraProperties={addToCartProperties}
          >
            {stepConfig.strings.atcText}
          </RechargeAddToCart>

          {state.subscriptionType === 'me' &&
            settings.steps.summary.strings.benefitsText && (
            <div className="sub-builder-summary-benefits">
              <h4 className="heading-4 sub-builder-summary-benefits-title">
                {settings.steps.summary.strings.benefitsTitle}
              </h4>
              <div className="rte">
                {ReactHtmlParser(settings.steps.summary.strings.benefitsText)}
              </div>
            </div>
          )}

          <div className="sub-builder-summary-review">
            <div className="review-stars">
              <IconReviewStar />
              <IconReviewStar />
              <IconReviewStar />
              <IconReviewStar />
              <IconReviewStar />
            </div>
            <h4 className="review-title">
              {settings.steps.summary.strings.reviewTitle}
            </h4>
            <p className="review-content">
              {settings.steps.summary.strings.reviewContent}
            </p>
            <div className="flex align-center space-between">
              <div className="review-name">
                - {settings.steps.summary.strings.reviewName}
              </div>
              <div className="review-brand flex align-center">
                <div className="review-rating">
                  {settings.steps.summary.strings.reviewRating}
                </div>
                {settings.steps.summary.settings.reviewLink && (
                  <a
                    href={settings.steps.summary.settings.reviewLink}
                    className="review-brand-link"
                  >
                    <img
                      src={getSizedImageUrl(
                        settings.steps.summary.settings.reviewLogo,
                        '72x20',
                      )}
                      data-lazy-load
                    />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Summary;
